<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  useTemplateRef,
} from 'vue';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskStepProgressIndicator> in <template>
import KioskStepProgressIndicator from '@/app/layouts/kiosk/KioskStepProgressIndicator.vue';
import { router } from '@/app/router';
// biome-ignore lint/correctness/noUnusedImports: used via tag <UploadedPhotosSidebar> in <template>
import UploadedPhotosSidebar from '@/components/UploadedPhotosSidebar.vue';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import {
  useWebcamCaptureFlow,
  WEBCAM_CAPTURE_STATES,
  type WebcamCaptureControlsHandle,
  type WebcamCaptureFlow,
  type WebcamCaptureState,
  type WebcamPhotoIndex,
  type WebcamTargets,
} from './photo-from-webcam/useWebcamCaptureFlow';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import WebcamCaptureControls from './photo-from-webcam/WebcamCaptureControls.vue';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import WebcamCaptureStage from './photo-from-webcam/WebcamCaptureStage.vue';

interface UsePhotoEvent {
  animateFromRef: HTMLElement;
  photoIndex: WebcamPhotoIndex;
}

interface PhotoFromWebcamPublicApi {
  readonly STATES: typeof WEBCAM_CAPTURE_STATES;
  countdown: number | null;
  currentState: WebcamCaptureState;
  readonly isShowingTakenPhoto: boolean;
  isPhotographingInProgress: boolean;
  isTakenPhotoLoaded: boolean;
  isUploadingInProgress: boolean;
  isWebcamReady: boolean;
  readonly lastTakenPhoto: unknown;
  launchWebcamCountdown: (
    stepsTotal: number,
    stepsLeft?: number | null
  ) => Promise<void>;
  onPhotoUploadAndAnimationComplete: () => void;
  onRetryPhotographingClick: () => void;
  onRetryWebcamInit: () => void;
  onTakePhotoClick: (photoIndex: WebcamPhotoIndex) => void;
  onTakenPhotoLoad: () => void;
  onUsePhotoClick: (
    photoIndex: WebcamPhotoIndex,
    animateFromRef: HTMLElement
  ) => Promise<void>;
  readonly showUploadFromPhoneButton: boolean;
  webcamInitFailed: boolean;
}

const layout = useTemplateRef<InstanceType<typeof KioskLayout>>('layout');
const stage = useTemplateRef<InstanceType<typeof WebcamCaptureStage>>('stage');
const controls =
  useTemplateRef<InstanceType<typeof WebcamCaptureControls>>('controls');

const flow = useWebcamCaptureFlow({
  getControls: (): WebcamCaptureControlsHandle | null => controls.value,
  getFlashTarget: (): HTMLElement | object => {
    if (!layout.value) {
      throw new Error('Webcam layout is not mounted');
    }
    return layout.value;
  },
  getWebcamTargets: (): WebcamTargets => {
    const targets = stage.value?.getWebcamTargets();
    if (!targets) {
      throw new Error('Webcam capture stage is not mounted');
    }
    return targets;
  },
});

const {
  countdown,
  currentState,
  enter,
  isPhotographingInProgress,
  isShowingTakenPhoto,
  isTakenPhotoLoaded,
  isUploadingInProgress,
  isWebcamReady,
  lastTakenPhoto,
  launchWebcamCountdown,
  leave,
  onPhotoUploadAndAnimationComplete,
  onTakenPhotoLoad,
  retryPhoto,
  retryWebcamInit,
  showUploadFromPhoneButton,
  takePhoto,
  usePhoto,
  webcamInitFailed,
}: WebcamCaptureFlow = flow;

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const uploadFromPhoneLink = computed((): string =>
  router.getLinkToStep('photo-from-phone', {
    query: { from: 'webcam' },
  })
);

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onUsePhoto({ photoIndex, animateFromRef }: UsePhotoEvent): void {
  void usePhoto(photoIndex, animateFromRef);
}

const publicApi: PhotoFromWebcamPublicApi = proxyRefs({
  STATES: WEBCAM_CAPTURE_STATES,
  countdown,
  currentState,
  get isShowingTakenPhoto(): boolean {
    return isShowingTakenPhoto.value;
  },
  isPhotographingInProgress,
  isTakenPhotoLoaded,
  isUploadingInProgress,
  isWebcamReady,
  get lastTakenPhoto(): unknown {
    return lastTakenPhoto.value;
  },
  launchWebcamCountdown,
  onPhotoUploadAndAnimationComplete,
  onRetryPhotographingClick: retryPhoto,
  onRetryWebcamInit: retryWebcamInit,
  onTakePhotoClick: takePhoto,
  onTakenPhotoLoad,
  onUsePhotoClick: usePhoto,
  get showUploadFromPhoneButton(): boolean {
    return showUploadFromPhoneButton.value;
  },
  webcamInitFailed,
});

onMounted((): void => {
  exposeToGlobalScope(publicApi, 'PhotoFromWebcam');
  void enter();
});

onBeforeUnmount(leave);

defineExpose(publicApi);
</script>

<template>
  <KioskLayout
    ref="layout"
    :classes="{ content: 'PhotoFromWebcam' }"
    :is-fullscreen="true"
  >
    <template #KioskLayout__right>
      <UploadedPhotosSidebar
        :fade="true"
        :watch="true"
        @animation-complete="onPhotoUploadAndAnimationComplete"
      />
    </template>

    <template #KioskLayout__bottom>
      <KioskStepProgressIndicator :index="0"/>
    </template>

    <WebcamCaptureStage
      ref="stage"
      :is-showing-taken-photo="isShowingTakenPhoto"
      :is-taken-photo-loaded="isTakenPhotoLoaded"
      :is-webcam-ready="isWebcamReady"
      :photo-url="lastTakenPhoto?.url"
      @photo-load="onTakenPhotoLoad"
    />

    <WebcamCaptureControls
      ref="controls"
      :countdown="countdown"
      :current-state="currentState"
      :is-photographing-in-progress="isPhotographingInProgress"
      :is-showing-taken-photo="isShowingTakenPhoto"
      :is-uploading-in-progress="isUploadingInProgress"
      :is-webcam-ready="isWebcamReady"
      :show-upload-from-phone-button="showUploadFromPhoneButton"
      :states="WEBCAM_CAPTURE_STATES"
      :upload-from-phone-link="uploadFromPhoneLink"
      :webcam-init-failed="webcamInitFailed"
      @retry-camera="retryWebcamInit"
      @retry-photo="retryPhoto"
      @take-photo="takePhoto"
      @use-photo="onUsePhoto"
    />
  </KioskLayout>
</template>

<style scoped lang="scss">
:global(.PhotoFromWebcam) {
  display: flex;
  justify-content: center;
  align-items: stretch;
}
</style>
